/**
 * TNK-155 phase B — resolve an inventory card by id without depending on
 * AppContext having the entire inventory loaded.
 *
 * Resolution order:
 *   1. AppContext.cards (warm cache from sign-in hydration). Free.
 *   2. Module-level cache populated by previous lookups in this session.
 *   3. Single-card fetch against `/api/cards/:id`.
 *
 * Mirrors `useStorefrontCardLookup` (TNK-124 phase G) but single-id and
 * operator-scoped. Phase D removes the AppContext eager hydration; this
 * hook continues working since step 2 + 3 carry the load on their own.
 *
 * Cache lifetime: per-page-load. A full refresh busts it and inventory
 * mutates slowly enough that staleness is rarely the cause of a bug.
 */
import { useEffect, useState } from "react";

import { type Card, useApp } from "@/context/AppContext";
import { fetchInventoryCard } from "@/context/inventorySync";

interface LookupResult {
  phase: "loading" | "ready" | "missing" | "error";
  card: Card | null;
}

// Shared across hook instances so cards seen on one page don't refetch
// when the operator navigates between detail views.
const cardCache = new Map<string, Card | null>();
const inflight = new Map<string, Promise<Card | null>>();

function fetchOnce(id: string): Promise<Card | null> {
  if (cardCache.has(id)) return Promise.resolve(cardCache.get(id) ?? null);
  const existing = inflight.get(id);
  if (existing) return existing;
  const p = fetchInventoryCard(id)
    .catch(() => null)
    .then((card) => {
      cardCache.set(id, card);
      inflight.delete(id);
      return card;
    });
  inflight.set(id, p);
  return p;
}

export function useInventoryCardLookup(id: string): LookupResult {
  const { cards } = useApp();
  const [extra, setExtra] = useState<Card | null | undefined>(() => undefined);
  const [errored, setErrored] = useState(false);

  // Reset when the id changes so we don't render the prior card while
  // the new one resolves.
  useEffect(() => {
    setExtra(undefined);
    setErrored(false);
  }, [id]);

  // Try the warm cache first; only fetch if absent.
  const fromContext = cards.find((c) => c.id === id) ?? null;

  useEffect(() => {
    if (fromContext) return;
    if (cardCache.has(id)) {
      setExtra(cardCache.get(id) ?? null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const card = await fetchOnce(id);
        if (!cancelled) setExtra(card);
      } catch {
        if (!cancelled) setErrored(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, fromContext]);

  if (fromContext) return { phase: "ready", card: fromContext };
  if (errored) return { phase: "error", card: null };
  if (extra === undefined) return { phase: "loading", card: null };
  if (extra === null) return { phase: "missing", card: null };
  return { phase: "ready", card: extra };
}
